import { computed, ComputedRef, Ref } from "vue";
import { useRoute } from "vue-router";

import type { Controller } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import {
  APP_SCREEN_TO_ORG_ROUTE,
  APP_SCREEN_TO_ROUTE,
  getAgentInfo,
  getAppScreenOrganizationId,
  getDatabaseInfo,
  getItemId,
  isUiAction,
  UI_MENU_ITEMS,
  type FrontendMenuItem,
} from "@/components/menu/menuItems";
import { Menu } from "@/composables/useMenu";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";
import { navigateToMenuItem } from "@/utils/navigation/menu";
import { openExternalUrl } from "@/utils/open-external-url";

// XXX[to remove in favor of the main useMenu composable]
export const useMenu = (
  menu: Menu,
  route: Ref<FrontendMenuItem>,
  // Org context for org-scoped app screens rendered inside an org section.
  orgId?: string
): {
  isRoute: ComputedRef<boolean>;
  goTo: (
    nonNavigationFunction?: () => void,
    event?: MouseEvent | KeyboardEvent
  ) => void;
} => {
  const router = inject.router();
  const currentRoute = useRoute();
  const controller = inject.controller();
  const profile = inject.profile();
  const baseLogger = inject.logger();

  const itemId = computed(() => getItemId(route.value));
  const itemOrgId = computed(
    () =>
      orgId ??
      getAppScreenOrganizationId(
        route.value,
        profile.value.user.personal.organizationId
      )
  );

  const isRoute = computed(() => {
    if (itemId.value === RouteNames.Agenda) {
      // Don't consider Agenda as selected when viewing an event
      return currentRoute.name === RouteNames.Agenda && !currentRoute.params.id;
    }

    // Org-scoped app screens rendered in an org section must always match
    // both the organization and their list route.
    if (itemOrgId.value && route.value.target.kind === "app_screen") {
      const orgRouteName = APP_SCREEN_TO_ORG_ROUTE[route.value.target.value];
      const globalRouteName = APP_SCREEN_TO_ROUTE[route.value.target.value];
      if (
        itemOrgId.value === profile.value.user.personal.organizationId &&
        currentRoute.name === globalRouteName
      ) {
        return true;
      }
      return (
        currentRoute.params.orgId === itemOrgId.value &&
        currentRoute.name === orgRouteName
      );
    }

    // Organization homepage: match route name and orgId param
    if (route.value.target.kind === "organization_homepage") {
      return (
        currentRoute.name === RouteNames.Organization &&
        currentRoute.params.orgId === itemId.value
      );
    }

    // Workspace homepage: match route name and workspaceId param
    if (route.value.target.kind === "workspace_homepage") {
      return (
        currentRoute.name === RouteNames.Workspace &&
        currentRoute.params.workspaceId === route.value.target.value
      );
    }

    // Database: match route name and params
    if (route.value.target.kind === "database") {
      const info = getDatabaseInfo(route.value);
      if (!info) return false;
      return (
        currentRoute.name === RouteNames.Database &&
        currentRoute.params.workspaceId === info.workspaceId &&
        currentRoute.params.databaseId === info.tableId
      );
    }

    // Agent: match route name and agentId param
    if (route.value.target.kind === "agent") {
      const info = getAgentInfo(route.value);
      if (!info) return false;
      return (
        currentRoute.name === RouteNames.Agent &&
        currentRoute.params.agentId === info.agentId
      );
    }

    if (currentRoute.name === itemId.value) {
      return true;
    }

    return false;
  });

  const goTo = (
    nonNavigationFunction?: () => void,
    event?: MouseEvent | KeyboardEvent
  ) => {
    // Handle UI-only items that don't navigate (Collapse, Search, Help)
    if (
      itemId.value === UI_MENU_ITEMS.Collapse ||
      itemId.value === UI_MENU_ITEMS.Search ||
      itemId.value === UI_MENU_ITEMS.Help
    ) {
      nonNavigationFunction?.();
      return;
    }

    // Handle Settings specially (it's a UI item that does navigate)
    if (itemId.value === UI_MENU_ITEMS.Settings) {
      // If already in a settings route, don't navigate
      if (currentRoute.name === RouteNames.Settings) {
        return;
      }
      navigateTo(RouteNames.Settings, router, undefined, event);
      return;
    }

    if (isUiAction(route.value)) return;

    void navigateToMenuItem(
      router,
      controller,
      profile,
      baseLogger,
      route.value,
      event,
      currentRoute.name,
      orgId
    );
  };

  return {
    isRoute,
    goTo,
  };
};

export function goToKBShortcuts(controller: Controller | null) {
  openExternalUrl(
    "https://help.routine.co/articles/6036979-keyboard-shortcuts",
    controller
  );
}
